import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setAccessToken } from '@adthlete/shared-frontend'
import { loginThunk, fetchCurrentUser } from '../redux/userSlice'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import athleteApi from '../lib/api'
import { AppDispatch } from '../store'

const useAppDispatch = () => useDispatch<AppDispatch>()

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [tokenValid, setTokenValid] = useState<boolean | null>(null)
  const [userInfo, setUserInfo] = useState<any>(null)

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link')
      setTokenValid(false)
      return
    }

    // Validate token on component mount
    const validateToken = async () => {
      try {
        const response = await athleteApi.post('/users/validate-reset-token', { token })
        setTokenValid(true)
        setUserInfo(response.data.user)
      } catch (err: any) {
        setError(err.response?.data?.error || 'Invalid or expired reset link')
        setTokenValid(false)
      }
    }

    validateToken()
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    setIsLoading(true)

    try {
      const response = await athleteApi.post('/users/reset-password', {
        token,
        password,
      })

      // The user is automatically logged in after successful password reset. Keep the access
      // token in memory (refresh token is set as an HTTP-only cookie) and load the profile.
      if (response.data.user && response.data.token) {
        setAccessToken(response.data.token)
        await dispatch(fetchCurrentUser())
        navigate('/')
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (tokenValid === false) {
    return (
      <div className="flex items-center justify-center min-h-screen relative">
        <div className="fixed inset-0 -z-10 bg-background"></div>

        <div className="w-full max-w-md px-4">
          <Card variant="dark">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <img
                  src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                  alt="Athios Logo"
                  className="w-10 h-10"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-foreground">
                Invalid Reset Link
              </CardTitle>
              <CardDescription className="mt-2 text-gray-600">
                This password reset link is invalid or has expired.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-red-500 mb-6">{error}</p>
              <div className="space-y-2">
                <Link
                  to="/forgot-password"
                  className="block text-[#4d7c0f] hover:text-[#a8e45a] font-medium"
                >
                  Request New Reset Link
                </Link>
                <Link to="/signin" className="block text-gray-600 hover:text-foreground text-sm">
                  Back to Sign In
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (tokenValid === null) {
    return (
      <div className="flex items-center justify-center min-h-screen relative">
        <div className="fixed inset-0 -z-10 bg-background"></div>

        <div className="w-full max-w-md px-4">
          <Card variant="dark">
            <CardContent className="text-center py-8">
              <div className="text-foreground">Validating reset link...</div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen relative">
      <div className="fixed inset-0 -z-10 bg-background"></div>

      <div className="w-full max-w-md px-4">
        <Card variant="dark">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img
                src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                alt="Athios Logo"
                className="w-10 h-10"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">Reset Password</CardTitle>
            <CardDescription className="mt-2 text-gray-600">
              {userInfo && `Reset password for ${userInfo.email}`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && <p className="text-red-500 mb-4 text-sm text-center">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="New Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-card text-foreground border-foreground/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                  required
                  minLength={6}
                />
              </div>
              <Button
                type="submit"
                variant="lime"
                className="w-full font-medium"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link
                to="/signin"
                className="text-[#4d7c0f] hover:text-[#a8e45a] font-medium text-sm"
              >
                Back to Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ResetPassword
